import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import { STATUSARRAY } from "../../_contstants/common";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import IconButton from "../../components/common/IconButton";
import {
  createTravelPurpose,
  getAllActiveTravelPurposes,
  getAllTravelPurpose,
  getTravelPurposeByID,
  updateTravelPurpose,
} from "./services/travelpurpose.services";
import { clearTravelPurposeDetails } from "./travelpurposeSlice";

const CreateTravelPurpose: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const [formKey, setFormKey] = React.useState(1);

  const TravelPurposeDetail = useAppSelector(
    (state) => state.travelpurpose.TravelPurposeDetail
  );
  const TravelPurposeID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector(
    (state) => state.travelpurpose.formLoading
  );
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);

  useEffect(() => {
    return () => {
      dispatch(clearTravelPurposeDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (TravelPurposeID) {
      dispatch(getTravelPurposeByID(TravelPurposeID));
    }
  }, [TravelPurposeID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [TravelPurposeDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (!TravelPurposeID) {
        const insertPayload = {
          TravelPurpose: values.TravelPurpose,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createTravelPurpose(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearTravelPurposeDetails());
          if (isIndirect) {
            dispatch(getAllActiveTravelPurposes());
          } else {
            dispatch(getAllTravelPurpose());
          }
        }
      } else {
        const editPayload = {
          ID: TravelPurposeID,
          TravelPurpose: values.TravelPurpose,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateTravelPurpose(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearTravelPurposeDetails());
          dispatch(getAllTravelPurpose());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={TravelPurposeDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem className="mui-drawer-header">
                <Typography.h4 className="m-0">
                  {TravelPurposeID
                    ? "Update Travel Purpose"
                    : "Add Travel Purpose"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="TravelPurpose"
                  label="Travel Purpose"
                  placeholder="i.e. Business"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="IsActive"
                  label="Status"
                  component={FormSelectionField}
                  astrike={true}
                  validator={requiredValidator}
                  placeholder="i.e. Active"
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status?.value,
                      label: status?.label,
                    };
                  })}
                />
              </GridLayoutItem>

              <GridLayoutItem
                colSpan={3}
                style={{
                  textAlign: "end",
                  marginTop: 20,
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={TravelPurposeID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />
                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateTravelPurpose;
